import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Tabs,
  Tab,
  Stack,
  Typography,
} from '@mui/material';
import authService from '../services/authService';

const initialRegisterState = {
  email: '',
  password: '',
  confirmPassword: '',
  displayName: '',
  companyName: '',
  businessSegment: '',
};

const AuthDialog = ({ open, onClose, onSuccess }) => {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState(initialRegisterState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const resetState = () => {
    setMode('login');
    setForm(initialRegisterState);
    setError('');
    setLoading(false);
  };

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      if (mode === 'login') {
        const user = await authService.login({
          email: form.email,
          password: form.password,
        });
        onSuccess?.(user);
      } else {
        if (form.password !== form.confirmPassword) {
          throw new Error('As senhas não conferem.');
        }
        const user = await authService.register({
          email: form.email,
          password: form.password,
          displayName: form.displayName,
          companyName: form.companyName,
          businessSegment: form.businessSegment,
        });
        onSuccess?.(user);
      }
      resetState();
    } catch (err) {
      setError(err.message || 'Erro ao autenticar.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetState();
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{mode === 'login' ? 'Entrar' : 'Criar conta'}</DialogTitle>
      <DialogContent dividers>
        <Tabs
          value={mode}
          onChange={(_event, value) => {
            setMode(value);
            setError('');
          }}
          variant="fullWidth"
          sx={{ mb: 2 }}
        >
          <Tab label="Entrar" value="login" />
          <Tab label="Cadastrar" value="register" />
        </Tabs>
        <Stack spacing={2}>
          {mode === 'register' && (
            <>
              <TextField
                label="Empresa"
                value={form.companyName}
                onChange={handleChange('companyName')}
                fullWidth
                autoFocus
              />
              <TextField
                label="Ramo de atividade"
                value={form.businessSegment}
                onChange={handleChange('businessSegment')}
                fullWidth
              />
              <TextField
                label="Nome do usuário"
                value={form.displayName}
                onChange={handleChange('displayName')}
                fullWidth
              />
            </>
          )}
          <TextField
            label="E-mail"
            type="email"
            value={form.email}
            onChange={handleChange('email')}
            fullWidth
            autoFocus={mode === 'login'}
          />
          <TextField
            label="Senha"
            type="password"
            value={form.password}
            onChange={handleChange('password')}
            fullWidth
          />
          {mode === 'register' && (
            <TextField
              label="Confirmar senha"
              type="password"
              value={form.confirmPassword}
              onChange={handleChange('confirmPassword')}
              fullWidth
            />
          )}
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Processando...' : mode === 'login' ? 'Entrar' : 'Cadastrar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuthDialog;
